import useMicControl from '@/composables/shared/mic/process/useMicControl';
import useSystemAudioMicControl from '@/composables/shared/mic/process/useSystemAudioMicControl';
import { i18n } from '@/plugins/i18n';
import { useMessageStore } from '@/stores/message';
import { useSocketStore } from '@/stores/socket';
import { useSocketsStore } from '@/stores/sockets';
import { useSystemAudioMicrophoneStore } from '@/stores/systemAudioMicrophone';
import { useToastStore } from '@/stores/toast.js';
import { useUserStore } from '@/stores/user';
import { MicType } from '@/types/components';
import { InboundMessageMM } from '@/types/socket/inbound';
import { OutboundSignalStatus } from '@/types/socket/message';
import { OutboundMessageMM } from '@/types/socket/outbound';
import { MIC_SIGNAL_TIMEOUT_MS } from '@/utils/constants/common';
import { generateTimeStamp } from '@/utils/generateTimeStamp';

export default function useMicSignal(micType: MicType) {
  const socketStore = useSocketStore();
  const socketsStore = useSocketsStore();
  const toastStore = useToastStore();
  const userStore = useUserStore();
  const messageStore = useMessageStore();
  const systemAudioMicrophoneStore = useSystemAudioMicrophoneStore();

  const micControl = useMicControl();
  const systemMicControl = useSystemAudioMicControl();

  const t = i18n.global.t;

  const micEvent = computed(() => {
    return (
      {
        MIC: { start: micControl.startMic, stop: micControl.stopMic },
        SYSTEM_AUDIO_MIC: { start: systemMicControl.startMic, stop: systemMicControl.stopMic },
      }[micType] ?? { start: () => {}, stop: () => {} }
    );
  });

  const micTimerType = computed(() => {
    return micType === 'SYSTEM_AUDIO_MIC' ? 'systemAudioMicTimer' : 'micTimer';
  });

  const startMicrophone = async () => {
    await sendRequestSignal();
  };

  const stopMicrophone = async () => {
    await sendOffSignal();
    await micEvent.value.stop();
  };

  /**
   * Outbound - REQUEST(요청), REJECT(불가), TIMEOUT(타임아웃), ACK(마이크종료확인)
   * */
  const dispatchMicrophoneSignal = async (signal: OutboundSignalStatus) => {
    const requestTimestamp = await generateTimeStamp();

    const message = {
      signal,
      requestTimestamp,
      isSystemAudio: micType === 'SYSTEM_AUDIO_MIC',
      requestUserId: userStore.userId,
    };

    if (micType === 'SYSTEM_AUDIO_MIC') {
      socketsStore.dispatchMessage<OutboundMessageMM>(systemAudioMicrophoneStore.socketId, 'mm', message);
    } else {
      socketStore.dispatchMessage<OutboundMessageMM>('mm', message);
    }
  };

  const sendRequestSignal = async () => {
    await dispatchMicrophoneSignal('REQUEST');
    startTimer(true);
  };

  const sendAckSignal = async () => {
    await dispatchMicrophoneSignal('ACK');
    clearTimer();
  };

  const sendTimeoutSignal = async () => {
    toastStore.error(i18n.global.t('error_mic_unavailable'));
    await dispatchMicrophoneSignal('TIMEOUT');
    clearTimer();
  };

  const sendOffSignal = async () => {
    await dispatchMicrophoneSignal('OFF');
    startTimer(false);
  };

  /**
   * Inbound - ACCEPT(수락), REJECT(불가), TIMEOUT(타임아웃), ACK(마이크종료확인)
   * */
  const handleMicSignal = (msg: InboundMessageMM) => {
    if (!['ACCEPT', 'REJECT', 'TIMEOUT', 'ACK'].includes(msg.signal)) return;

    const signalEvent = {
      ACCEPT: handleAcceptSignal,
      REJECT: handleRejectSignal,
      ACK: handleAckSignal,
    }[msg.signal];

    if (signalEvent) {
      signalEvent();
    }
  };

  const handleAcceptSignal = async () => {
    await sendAckSignal();
    await micEvent.value.start();
  };

  const handleRejectSignal = () => {
    toastStore.warn(t('mic_muted_by_other_host'));
  };

  const handleAckSignal = () => {
    clearTimer();
  };

  const startTimer = (isOn: boolean) => {
    clearTimer();
    messageStore.patchStatus(
      micTimerType.value,
      setTimeout(async () => {
        if (isOn) {
          await sendTimeoutSignal();
        } else {
          await sendOffSignal();
        }
      }, MIC_SIGNAL_TIMEOUT_MS)
    );
  };
  const clearTimer = () => {
    if (messageStore.status[micTimerType.value]) {
      clearTimeout(messageStore.status[micTimerType.value]);
      messageStore.patchStatus(micTimerType.value, null);
    }
  };

  return {
    startMicrophone,
    stopMicrophone,
    handleMicSignal,
  };
}
